import { useState, useEffect, useCallback, useRef } from "react";
import { Header, Card, Text, ScrollBox } from "../../Components/Index";
import { useNotifications } from "../../Context/NotificationContext";
import { fetchNotificationsData } from "../../services/notificationService";
import {
  NotificationList,
  LoadingState,
} from "./components";

// Notifications page: list is driven by the shared notification store
// (GET /reminders/due + the local Next Service Mileage mirror) so the page
// and the Header badge always show the same REAL data with a single
// request. Outside a NotificationProvider (e.g. isolated tests) it falls
// back to a direct fetch of the same shared service.
// When there is nothing to show, exactly one empty box with
// «پیامی ثبت نشده» is rendered (no cards, no update button).
function Notifications() {
  const store = useNotifications();
  const [localNotifications, setLocalNotifications] = useState([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const storeRefresh = store?.refresh;
  const refreshedOnce = useRef(false);

  useEffect(() => {
    // Provider mode: exactly one shared refresh per page entry serves both
    // the page and the Header badge. Concurrent refresh calls share a
    // single in-flight request (no duplicated API requests, no polling).
    if (store !== null) {
      if (!refreshedOnce.current) {
        refreshedOnce.current = true;
        storeRefresh?.();
      }
      return undefined;
    }
    // Fallback mode (no provider): same shared service, same data shape.
    let cancelled = false;
    setLocalLoading(true);
    fetchNotificationsData()
      .then((data) => {
        if (!cancelled) {
          setLocalNotifications(Array.isArray(data) ? data : []);
          setLocalLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLocalNotifications([]);
          setLocalLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [store, storeRefresh]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const notifications =
    store !== null ? store.notifications : localNotifications;
  const loading = store !== null ? store.loading : localLoading;
  const showList = !loading && notifications.length > 0;
  const showEmpty = !loading && notifications.length === 0;

  return (
    <div className="nt-page">
      <Header Text="اعلان‌ها" hideR="" LinkR="/" />

      {loading && (
        <div className="nt-status">
          <LoadingState />
        </div>
      )}

      {showEmpty && (
        <Card className="nt-empty" data-testid="notifications-empty">
          <Text position="center" FontSize="md">
            پیامی ثبت نشده
          </Text>
        </Card>
      )}

      {showList && (
        <ScrollBox height="100%" className="nt-scroll">
          <NotificationList
            notifications={notifications}
            expandedIds={expandedIds}
            onToggle={toggleExpand}
          />
        </ScrollBox>
      )}
    </div>
  );
}

export default Notifications;
